import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';

import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import vatRules from '../vat-validation-rules.json';
import config from '../config';

import './CheckoutCustomerDetails.css';

countries.registerLocale(enLocale);

const CheckoutCustomerDetails = ({ product, onContinue, initialData }) => {
    const [formData, setFormData] = useState(initialData || {
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        vatNumber: '',
        firstLine: '',
        city: '',
        region: '',
        countryCode: '',
        zip: '',
        termsAccepted: false,
        purchaseForCompany: false,
        secondLine: '',
        marketingConsent: false
    });

    const [vatWarning, setVatWarning] = useState('');
    const [errors, setErrors] = useState({});
    const [isValidatingEmail, setIsValidatingEmail] = useState(false);

    const countryOptions = useMemo(() => {
        const names = countries.getNames('en', { select: 'official' });
        return Object.entries(names)
            .map(([code, name]) => ({ code, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, []);

    useEffect(() => {
        if (formData.purchaseForCompany && !formData.vatNumber) {
            setVatWarning('VAT number is missing. You may be charged VAT.');
            return;
        }

        if (!formData.vatNumber || !formData.countryCode) {
            setVatWarning('');
            return;
        }

        const countryRule = vatRules.find(rule => rule.countryCode === formData.countryCode);

        if (countryRule) {
            const isValid = countryRule.rules.some(rule => {
                const regex = new RegExp(`^${rule}$`);
                return regex.test(formData.vatNumber);
            });

            if (!isValid) {
                setVatWarning('VAT number does not match the expected format for the selected country.');
            } else {
                setVatWarning('');
            }
        } else {
            setVatWarning('');
        }
    }, [formData.vatNumber, formData.countryCode, formData.purchaseForCompany]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
        // Clear error when user types
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.email) newErrors.email = 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.firstLine) newErrors.firstLine = 'Address is required';
        if (!formData.city) newErrors.city = 'City is required';
        if (!formData.region) newErrors.region = 'State/Region is required';
        if (!formData.countryCode) newErrors.countryCode = 'Country is required';
        if (!formData.zip) newErrors.zip = 'ZIP/Postal Code is required';
        if (!formData.termsAccepted) newErrors.termsAccepted = 'You must accept the terms';


        if (formData.purchaseForCompany) {
            if (!formData.company) newErrors.company = 'Company name is required';
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        // Academic Validation
        if (product && product.sku && product.sku.startsWith('EDU')) {
            setIsValidatingEmail(true);
            try {
                const response = await axios.post(config.academicEmailValidationUrl, { email: formData.email });
                if (!response.data || response.data.data !== true) {
                    setErrors({ ...validationErrors, email: 'Please provide a valid academic email address.' });
                    setIsValidatingEmail(false);
                    return;
                }
            } catch (err) {
                console.error("Email validation error", err);
                setErrors({ ...validationErrors, email: 'Unable to validate academic status. Please try again later.' });
                setIsValidatingEmail(false);
                return;
            }
            setIsValidatingEmail(false);
        }

        onContinue(formData);
    };

    return (
        <div className="checkout-step active">
            <div className="step-header">
                <div className="step-number-container">
                    <div className="step-number">2</div>
                </div>
                <h3>Customer details</h3>
            </div>
            <div className="step-content">
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h4>Enter account information</h4>
                        <p className="form-note">All fields required unless noted.</p>

                        <div className="form-group">
                            <label>Email address</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={isValidatingEmail} />
                            {errors.email && <p className="error">{errors.email}</p>}
                            {isValidatingEmail && <p className="info">Validating academic email...</p>}
                        </div>
                    </div>

                    <div className="form-section">
                        <h4>Customer information</h4>
                        <div className="form-group checkbox-group">
                            <input
                                type="checkbox"
                                name="purchaseForCompany"
                                checked={formData.purchaseForCompany}
                                onChange={handleChange}
                                id="purchaseForCompany"
                            />
                            <label htmlFor="purchaseForCompany">Purchase for a company</label>
                        </div>

                        {formData.purchaseForCompany && (
                            <>
                                <div className="form-group">
                                    <label>Company name</label>
                                    <input type="text" name="company" value={formData.company} onChange={handleChange} />
                                    {errors.company && <p className="error">{errors.company}</p>}
                                </div>
                                <div className="form-group">
                                    <label>VAT Number (Optional)</label>
                                    <input type="text" name="vatNumber" value={formData.vatNumber} onChange={handleChange} />
                                    {errors.vatNumber && <p className="error">{errors.vatNumber}</p>}
                                    {vatWarning && <p className="warning">{vatWarning}</p>}
                                </div>
                            </>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name</label>
                                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} />
                                {errors.firstName && <p className="error">{errors.firstName}</p>}
                            </div>
                            <div className="form-group">
                                <label>Last Name</label>
                                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} />
                                {errors.lastName && <p className="error">{errors.lastName}</p>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Country</label>
                            <select name="countryCode" value={formData.countryCode} onChange={handleChange}>
                                <option value="" disabled>Select Country</option>
                                {countryOptions.map((country) => (
                                    <option key={country.code} value={country.code}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                            {errors.countryCode && <p className="error">{errors.countryCode}</p>}
                        </div>

                        <div className="form-group">
                            <label>Address Line 1</label>
                            <input type="text" name="firstLine" value={formData.firstLine} onChange={handleChange} />
                            {errors.firstLine && <p className="error">{errors.firstLine}</p>}
                        </div>

                        <div className="form-group">
                            <label>Address Line 2 (Optional)</label>
                            <input type="text" name="secondLine" value={formData.secondLine} onChange={handleChange} />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>City</label>
                                <input type="text" name="city" value={formData.city} onChange={handleChange} />
                                {errors.city && <p className="error">{errors.city}</p>}
                            </div>
                            <div className="form-group">
                                <label>State/Region</label>
                                <input type="text" name="region" value={formData.region} onChange={handleChange} />
                                {errors.region && <p className="error">{errors.region}</p>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Postal code</label>
                            <input type="text" name="zip" value={formData.zip} onChange={handleChange} />
                            {errors.zip && <p className="error">{errors.zip}</p>}
                        </div>
                    </div>

                    <div className="form-group checkbox-group terms-group">
                        <input type="checkbox" name="termsAccepted" checked={formData.termsAccepted} onChange={handleChange} id="termsAccepted" />
                        <label htmlFor="termsAccepted">
                            I accept the <a href="https://spherene.io/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, <a href="https://spherene.io/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Use</a>, <a href="https://spherene.io/refund-policy" target="_blank" rel="noopener noreferrer">Refund Policy</a>, and <a href="https://spherene.io/license-agreement-eula-spherene-ag" target="_blank" rel="noopener noreferrer">End User License Agreement</a>.
                        </label>
                        {errors.termsAccepted && <p className="error">{errors.termsAccepted}</p>}
                    </div>

                    <div className="form-group checkbox-group terms-group">
                        <input
                            type="checkbox"
                            name="marketingConsent"
                            checked={formData.marketingConsent}
                            onChange={handleChange}
                            id="marketingConsent"
                        />
                        <label htmlFor="marketingConsent">I want to receive emails, offers, and information about product updates and upgrades.<br /> I understand that I will need to confirm my subscription</label>
                        {errors.marketingConsent && <p className="error">{errors.marketingConsent}</p>}
                    </div>

                    <button type="submit" className="continue-btn" disabled={isValidatingEmail}>
                        {isValidatingEmail ? 'Validating...' : 'Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CheckoutCustomerDetails;
